import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

//Import Components
import Button from '../../Components/ui/Button';
import ShippingFeeConsult from '../../Components/ui/ShippingFeeConsult';
import { 
  Package, 
  ShoppingBag, 
  MapPin, 
  Phone, 
  Mail, 
  User, 
  Calendar,
  CheckCircle, 
  ArrowLeft,
  Loader
} from 'lucide-react';

const OrderConfirmationPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const orderItems = JSON.parse(localStorage.getItem('orderItems')) || [];
  const [order, setOrder] = useState(JSON.parse(localStorage.getItem('pendingDetails')) || {});
  const reference = new URLSearchParams(location.search).get('reference');

  const useVerifyPayment = (type) => {
    const [status, setStatus] = useState('verifying');
    
    useEffect(() => {
      let isSubscribed = true;
      let verificationTimeout;
  
      const verifyPayment = async () => {
        // console.log(`Starting ${type} verification for reference:`, reference); // debug log

        if (!reference) return;
  
        try {
          const pendingDetails = JSON.parse(localStorage.getItem('pendingDetails'));
          if (!pendingDetails) {
            console.log('No pending details found');
            if (isSubscribed) setStatus('failed');
            return;
          }
  
          const response = await axios.post(`${process.env.REACT_APP_API_URL}/payment/verify`, {
            reference,
            paymentDetails: pendingDetails
          });
          
          if (isSubscribed) {
            if (response.data.status) {
              console.log('Payment verification successful');
              setStatus('success');
              localStorage.removeItem('pendingDetails');
            } else {
              console.log('Payment verification failed');
              setStatus('failed');
            }
          }
        } catch (error) {
          console.error('Verification error:', error);
          if (isSubscribed) setStatus('failed');
        }
      };
  
      // Add a small delay before verification to prevent rapid duplicate requests
      verificationTimeout = setTimeout(verifyPayment, 100);
  
      return () => {
        isSubscribed = false;
        clearTimeout(verificationTimeout);
      };
    }, [location, type]);
  
    return status;
  };

  const status = useVerifyPayment('order');

  if (status === 'verifying') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Verifying Payment</h2>
          <p className="text-gray-600">Please wait while we confirm your order...</p>
        </div>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Payment Verification Failed</h2>
          <p className="text-gray-600 mb-6">We couldn't verify your payment. Please try again or contact support.</p>
          <Button
            variant="accent"
            onClick={() => navigate('/ecommerce')}
            className="inline-flex items-center px-4 py-2"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Return to Shop
          </Button>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Order Not Found</h2>
          <Button
            variant="accent"
            onClick={() => navigate('/ecommerce')}
            className="inline-flex items-center px-4 py-2"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Return to Shop
          </Button>
        </div>
      </div>
    );
  }

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 pb-16 sm:py-12 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">

        {/* Order Success Header */}
        <div className="text-center mb-6 sm:mb-8">
          <div className="flex justify-center mb-3 sm:mb-4">
            <CheckCircle className="w-12 h-12 sm:w-16 sm:h-16 mt-10 md:mt-14 text-green-500" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Order Confirmed!</h1>
          <p className="text-sm sm:text-base text-gray-600 px-4">
            Thank you for your purchase. Your order has been successfully placed.
          </p>
        </div>

        {/* Shipping Fee Consultation CTA */}
        <ShippingFeeConsult orderDetails={{
          name: order.customerName,
          reference: reference,
          shippingAddress: order.shippingAddress,
          orderItems: order.orderItems
        }} />

        {/* Order Details Card */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {/* Order Header */}
          <div className="border-b border-gray-200 p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center">
                <Package className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 mr-2" />
                <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
                  Order #{reference}
                </h2>
              </div>
              <div className="flex items-center">
                <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 mr-2" />
                <span className="text-xs sm:text-sm text-gray-500">
                  {formatDate(order.orderDate)}
                </span>
              </div>
            </div>
          </div>

          {/* Customer Information */}
          <div className="p-4 sm:p-6 border-b border-gray-200">
            <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4">
              Customer Information
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-start">
                <User className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 mr-2 mt-1" />
                <div>
                  <p className="text-xs sm:text-sm text-gray-600">Name</p>
                  <p className="text-sm sm:text-base font-medium text-gray-800">
                    {order.customerName}
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 mr-2 mt-1" />
                <div>
                  <p className="text-xs sm:text-sm text-gray-600">Email</p>
                  <p className="text-sm sm:text-base font-medium text-gray-800 break-all">
                    {order.customerEmail}
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 mr-2 mt-1" />
                <div>
                  <p className="text-xs sm:text-sm text-gray-600">Phone</p>
                  <p className="text-sm sm:text-base font-medium text-gray-800">
                    {order.customerPhone}
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 mr-2 mt-1" />
                <div>
                  <p className="text-xs sm:text-sm text-gray-600">Shipping Address</p>
                  <p className="text-sm sm:text-base font-medium text-gray-800">
                    {order.shippingAddress}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Order Items */}
          <div className="p-4 sm:p-6 border-b border-gray-200">
            <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4">
              <ShoppingBag className="w-4 h-4 sm:w-5 sm:h-5 inline-block mr-2 text-gray-500" />
              Order Items
            </h3>
            <div className="space-y-4">
              {orderItems?.length > 0 ? (
                order.orderItems.map((item, index) => (
                  <div 
                    key={index} 
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-2 gap-3"
                  >
                    <div className="flex items-center space-x-3 sm:space-x-4">
                      <img
                        src={item.productImage}
                        alt={item.productName}
                        className="w-12 h-12 sm:w-16 sm:h-16 object-cover rounded-md"
                      />
                      <div>
                        <h4 className="text-sm sm:text-base font-medium text-gray-800">
                          {item.productName}
                        </h4>
                        <p className="text-xs sm:text-sm text-gray-600">
                          Size: {item.productSize.size} | Quantity: {item.productQuantity}
                        </p>
                      </div>
                    </div>
                    <p className="text-sm sm:text-base font-medium text-gray-800 ml-16 sm:ml-0">
                      ₦{(item.productSize.price * item.productQuantity).toLocaleString()}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-sm sm:text-base text-gray-600 text-center">No items found</p>
              )}
            </div>  
          </div>

          {/* Order Summary */}
          <div className="p-4 sm:p-6 bg-gray-50">
            <div className="flex justify-between items-center">
              <h3 className="text-base sm:text-lg font-semibold text-gray-800">Total Amount</h3>
              <p className="text-xl sm:text-2xl font-bold text-primary-dark">
                ₦{order.amount.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row justify-center space-y-3 sm:space-y-0 sm:space-x-4">
          <Button
            variant="accent"
            onClick={() => navigate('/ecommerce')}
            className="w-full sm:w-auto inline-flex items-center justify-center px-4 sm:px-6 py-2 sm:py-3"
          >
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
            Continue Shopping
          </Button>
          <Button
            variant="primary"
            onClick={() => navigate('/')}
            className="w-full sm:w-auto inline-flex items-center justify-center px-4 sm:px-6 py-2 sm:py-3"
          >
            Explore More
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmationPage;